import { useSearchParams } from 'react-router';

export type Spremembe = Record<string, string | null | undefined>;
export const VELIKOSTI = ['25', '50', '100', '200'];

/**
 * Stanje seznama po straneh na strežniku v URL-ju: iskanje
 * ({@code q}), razvrščanje ({@code razvrsti}/{@code smer}), stran (od 1) in velikost (25/50/100/200) ter poljubni
 * filtri. Vsak seznam ga uporablja enako, da se obnašajo enako (Nazaj, deljenje povezave).
 */
export function useSeznam<R extends string = string>() {
  const [parametri, setParametri] = useSearchParams();
  const velikostP = parametri.get('velikost') ?? '';
  const velikost = VELIKOSTI.includes(velikostP) ? Number(velikostP) : 25;
  const stran = Math.max(1, Number(parametri.get('stran') ?? '1') || 1);
  const razvrsti = (parametri.get('razvrsti') as R | null) ?? undefined;
  const smer: 'asc' | 'desc' = parametri.get('smer') === 'desc' ? 'desc' : 'asc';

  /**
   * Spremeni parametre; ob spremembi pogojev gremo na prvo stran. Kar je odvisno od trenutnih parametrov (razvrščanje,
   * listanje), se izračuna iz trenutnega URL-ja: React Router funkciji poda parametre zadnjega izrisa, zato bi hitra
   * zaporedna klika brala staro stanje (preverja E2E test razvrščanja).
   */
  const nastavi = (spremembe: Spremembe | ((trenutni: URLSearchParams) => Spremembe), naPrvo = true) => {
    const p = new URLSearchParams(window.location.search);
    const s = typeof spremembe === 'function' ? spremembe(new URLSearchParams(p)) : spremembe;
    Object.entries(s).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    if (naPrvo) {
      p.delete('stran');
    }
    setParametri(p, { replace: true });
  };

  /** Klik na glavo: naraščajoče → padajoče → privzeto. */
  const razvrscaj = (kljuc: R) =>
    nastavi((t) => {
      if (t.get('razvrsti') !== kljuc) {
        return { razvrsti: kljuc, smer: 'asc' };
      }
      return t.get('smer') === 'desc' ? { razvrsti: null, smer: null } : { smer: 'desc' };
    });

  /** Listanje za {@code korak} strani (največ do {@code strani}). */
  const listaj = (korak: number, strani: number) =>
    nastavi((t) => {
      const nova = Math.min(strani, Math.max(1, (Number(t.get('stran') ?? '1') || 1) + korak));
      return { stran: nova > 1 ? String(nova) : null };
    }, false);

  return {
    parameter: (ime: string) => parametri.get(ime),
    iskanje: parametri.get('q') ?? '',
    razvrsti,
    smer,
    stran,
    velikost,
    zamik: (stran - 1) * velikost,
    /** Parametri strani in razvrščanja za API (stran od 0). */
    zaApi: { razvrsti, smer: razvrsti ? smer : undefined, stran: stran - 1, velikost },
    nastavi,
    razvrscaj,
    listaj,
  };
}

export type StanjeSeznama<R extends string = string> = ReturnType<typeof useSeznam<R>>;
/** Del stanja brez razvrščanja – za filtre, iskanje in strani (ne glede na ključe razvrščanja seznama). */
export type OsnovaSeznama = Omit<StanjeSeznama, 'razvrscaj' | 'razvrsti' | 'zaApi'>;

/** Vrednost filtra Vsi/Da/Ne (komponenta DaNe) za API: 0 = vsi, 1 = da, 2 = ne. */
export function daNeZaApi(vrednost: string | null): number {
  return vrednost === '1' ? 1 : vrednost === '2' ? 2 : 0;
}
